/**
 * Web-only engagement timeline (plan / reason / execute).
 * Pure UI simulation — does not call backend or Agent SLBK tools.
 */

function cloneSteps(steps) {
  return steps.map((step) => ({
    kind: step.kind,
    label: step.label,
    detail: step.detail || '',
    status: step.status || 'pending'
  }))
}

export function buildCatalogSearchSteps({
  q = '',
  domestic = false,
  vendor = '',
  memoryMin,
  fp16Min,
  fp8Min
} = {}) {
  const query = String(q || '').trim()
  const filters = []
  if (domestic) filters.push('国产')
  if (vendor) filters.push(vendor)
  if (memoryMin != null && memoryMin !== '') filters.push(`显存≥${memoryMin}GB`)
  if (fp16Min != null && fp16Min !== '') filters.push(`FP16≥${fp16Min}T`)
  if (fp8Min != null && fp8Min !== '') filters.push(`FP8≥${fp8Min}T`)

  const steps = [
    {
      kind: 'plan',
      label: '规划检索路径',
      detail: filters.length
        ? `关键词结合筛选条件：${filters.join(' · ')}`
        : '先按关键词匹配，再按卡库指标整理结果'
    },
    {
      kind: 'reason',
      label: '理解查询意图',
      detail: query
        ? `正在围绕「${query}」查找合适的算力卡`
        : `按当前筛选收窄卡库（${filters.join(' · ') || '默认条件'}）`
    },
    {
      kind: 'tool',
      label: '检索卡库',
      detail: query ? `关键词：${query}` : '按筛选条件查询入库卡型'
    }
  ]

  if (filters.length) {
    steps.push({
      kind: 'tool',
      label: '应用指标筛选',
      detail: filters.join(' · ')
    })
  }

  steps.push({
    kind: 'reason',
    label: '汇总展示结果',
    detail: '整理参数徽章，并带上相关资讯与讨论'
  })

  return cloneSteps(steps)
}

export function buildPriceSearchSteps({ q = '' } = {}) {
  const query = String(q || '').trim()
  return cloneSteps([
    {
      kind: 'plan',
      label: '规划价格检索',
      detail: '对齐官方、市场与二手参考价'
    },
    {
      kind: 'reason',
      label: '锁定目标卡型',
      detail: query ? `查找「${query}」相关报价` : '等待关键词'
    },
    {
      kind: 'tool',
      label: '查询报价库',
      detail: query ? `关键词：${query}` : ''
    },
    {
      kind: 'tool',
      label: '合并价格时间轴',
      detail: '官方 · 市场 · 二手'
    },
    {
      kind: 'reason',
      label: '整理报价表',
      detail: '按月份与来源展示，并准备走势图'
    }
  ])
}

/** Map advise SSE step text → engagement rail (display only). */
export function buildAdviseProcessSteps(serverStep = '') {
  const text = String(serverStep || '').trim()
  const phases = [
    {
      hit: /理解|需求|提交/,
      plan: { kind: 'plan', label: '拆解选型需求', detail: '场景 · 国产偏好 · 预算' },
      tools: [{ kind: 'tool', label: '解析需求描述', detail: '从自然语言提取约束' }],
      reason: { kind: 'reason', label: '确认约束条件', detail: text || '正在理解需求' }
    },
    {
      hit: /查卡|价格|卡库/,
      plan: { kind: 'plan', label: '检索卡库与报价', detail: '只用站内已入库数据' },
      tools: [
        { kind: 'tool', label: '检索卡库', detail: '匹配参数与国产属性' },
        { kind: 'tool', label: '读取参考价', detail: '官方与市场价格' }
      ],
      reason: { kind: 'reason', label: '对照需求打分', detail: text || '正在查卡库和价格' }
    },
    {
      hit: /结论|推荐|撰写|写/,
      plan: { kind: 'plan', label: '生成选型结论', detail: '首选 + 备选 + 理由' },
      tools: [{ kind: 'tool', label: '撰写推荐说明', detail: '输出可读结论' }],
      reason: { kind: 'reason', label: '整理最终推荐', detail: text || '正在写结论' }
    }
  ]

  let activeIndex = 0
  for (let i = 0; i < phases.length; i += 1) {
    if (phases[i].hit.test(text)) activeIndex = i
  }
  if (!text) activeIndex = 0

  const steps = []
  phases.forEach((phase, index) => {
    const status = index < activeIndex ? 'done' : index === activeIndex ? 'active' : 'pending'
    steps.push({ ...phase.plan, status })
    phase.tools.forEach((tool) => {
      steps.push({ ...tool, status })
    })
    steps.push({ ...phase.reason, status })
  })
  return steps
}

export function finishAdviseProcessSteps(steps) {
  return (steps || []).map((step) => ({ ...step, status: 'done' }))
}

/** Map bid SSE step text → engagement rail (display only). */
export function buildBidProcessSteps(serverStep = '') {
  const text = String(serverStep || '').trim()
  const phases = [
    {
      hit: /解析|读取|上传|提交|抽取|识别/,
      plan: { kind: 'plan', label: '拆解招标文本', detail: '技术要求 · 约束条件' },
      tools: [{ kind: 'tool', label: '解析招标文件', detail: '提取显存、算力、功耗等约束' }],
      reason: { kind: 'reason', label: '确认约束清单', detail: text || '正在识别约束' }
    },
    {
      hit: /匹配|打分|卡库|对照|评分/,
      plan: { kind: 'plan', label: '对照卡库打分', detail: '只用站内已入库卡型' },
      tools: [
        { kind: 'tool', label: '检索卡库', detail: '按约束筛选候选' },
        { kind: 'tool', label: '计算匹配分', detail: '满分与缺口项' }
      ],
      reason: { kind: 'reason', label: '排序候选卡型', detail: text || '正在匹配卡型' }
    },
    {
      hit: /报告|导出|完成|结论|撰写/,
      plan: { kind: 'plan', label: '整理分析结论', detail: '满分卡 · 缺口说明' },
      tools: [{ kind: 'tool', label: '生成分析摘要', detail: '可供导出 PDF / Word / PPT' }],
      reason: { kind: 'reason', label: '汇总最终结果', detail: text || '正在整理报告' }
    }
  ]

  let activeIndex = 0
  for (let i = 0; i < phases.length; i += 1) {
    if (phases[i].hit.test(text)) activeIndex = i
  }
  if (!text) activeIndex = 0

  const steps = []
  phases.forEach((phase, index) => {
    const status = index < activeIndex ? 'done' : index === activeIndex ? 'active' : 'pending'
    steps.push({ ...phase.plan, status })
    phase.tools.forEach((tool) => {
      steps.push({ ...tool, status })
    })
    steps.push({ ...phase.reason, status })
  })
  return steps
}

export function finishBidProcessSteps(steps) {
  return (steps || []).map((step) => ({ ...step, status: 'done' }))
}

/**
 * Advance steps pending → active → done on a timer.
 * Call finish() when the real request completes.
 */
export function createProcessRunner(initialSteps, { onUpdate, intervalMs = 420 } = {}) {
  const list = cloneSteps(initialSteps)
  let index = 0
  let timer = null
  let stopped = false

  const emit = () => {
    if (typeof onUpdate === 'function') onUpdate(cloneSteps(list))
  }

  const clear = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  const tick = () => {
    if (stopped) return
    if (index > 0 && list[index - 1]) list[index - 1].status = 'done'
    if (index < list.length) {
      list[index].status = 'active'
      index += 1
      emit()
      timer = setTimeout(tick, intervalMs)
      return
    }
    emit()
  }

  return {
    start() {
      stopped = false
      index = 0
      list.forEach((step) => {
        step.status = 'pending'
      })
      emit()
      tick()
    },
    finish() {
      stopped = true
      clear()
      list.forEach((step) => {
        step.status = 'done'
      })
      emit()
    },
    stop() {
      stopped = true
      clear()
    }
  }
}
